import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const SUPPORT_EMAIL = "support@aigrowthoperator.xyz";
const LEGAL_EMAIL = "legal@aigrowthoperator.xyz";
const PRIVACY_EMAIL = "privacy@aigrowthoperator.xyz";
const COMPANY = "AI Growth Operator";
const PLATFORM_URL = "https://platform.aigrowthoperator.xyz";

const Shell = ({ title, lastUpdated, testid, children }) => (
  <div
    data-testid={testid}
    className="min-h-screen bg-white text-slate-900"
    style={{
      fontFamily:
        "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    }}
  >
    <header className="border-b border-slate-200">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="legal-back-home"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold tracking-[0.18em] uppercase">
          <ShieldCheck className="w-4 h-4" /> Legal
        </div>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
      <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">
        {title}
      </h1>
      <p className="mt-3 text-slate-500 text-sm">Last updated: {lastUpdated}</p>
      <div className="prose prose-slate max-w-none mt-8 prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-p:text-slate-700 prose-p:leading-relaxed prose-li:text-slate-700 prose-li:my-1 prose-a:text-emerald-700 prose-a:font-semibold prose-strong:text-slate-900 prose-code:bg-slate-100 prose-code:text-slate-900 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em] prose-code:before:hidden prose-code:after:hidden">
        {children}
      </div>
    </main>

    <footer className="border-t border-slate-200 mt-10">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-sm text-slate-500">
        <div>© {new Date().getFullYear()} · {COMPANY}</div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" data-testid="legal-link-privacy" className="hover:text-slate-900 transition">Privacy</Link>
          <Link to="/terms" data-testid="legal-link-terms" className="hover:text-slate-900 transition">Terms</Link>
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="legal-link-support" className="hover:text-slate-900 transition">Contact</a>
        </div>
      </div>
    </footer>
  </div>
);

export const Privacy = () => (
  <Shell title="Privacy Policy" lastUpdated="February 1, 2026" testid="privacy-page">
    <p>
      This Privacy Policy describes how {COMPANY} (“we”, “our”, “us”) collects,
      uses, and shares information when you use our website and Shopify
      application (collectively, the “Service”).
    </p>

    <h2>1. Information We Collect</h2>
    <h3>Account &amp; Merchant Data</h3>
    <p>
      When you install the AI Growth Operator app on Shopify or sign up via
      email, we collect: your store URL, email address, store name, the
      Shopify API access token issued by Shopify after you approve scopes, and
      preferences such as your daily-briefing send time and timezone.
    </p>
    <h3>Commerce Data (via Shopify APIs)</h3>
    <p>
      With your explicit approval, we read commerce data from Shopify such as
      products, orders, customers, and inventory snapshots. We do not modify
      your store data without an explicit “Approve” action from an authorized
      user.
    </p>
    <h3>Ad Account Data (via Google Ads)</h3>
    <p>
      If you choose to connect Google Ads, we read campaign performance and
      spend data via the Google Ads API. Connection is read-only by default;
      any campaign change requires your explicit approval.
    </p>
    <h3>Operational Data</h3>
    <p>
      Logs, error reports, IP address, and approximate location for security,
      fraud-prevention, and abuse-prevention purposes. We use cookies and
      local storage to keep you signed in to the admin dashboard.
    </p>

    <h2>2. How We Use Information</h2>
    <ul>
      <li>To deliver the Service — including profit-leak audits, daily 7&nbsp;AM Profit Briefings, recommendations, and one-click approvals.</li>
      <li>To send transactional and product emails (e.g., your daily briefing, install confirmations, billing receipts).</li>
      <li>To improve our models in an aggregated, de-identified form. We do not sell your data, and we do not use your commerce data to train third-party foundation models.</li>
      <li>To detect, prevent, and respond to security incidents, fraud, or abuse.</li>
      <li>To comply with legal obligations.</li>
    </ul>

    <h2>3. Sharing &amp; Sub-processors</h2>
    <p>We share data only with vetted sub-processors necessary to operate the Service:</p>
    <ul>
      <li><strong>Shopify</strong> — store data exchange via official OAuth.</li>
      <li><strong>Google Ads</strong> — ad account read access via official OAuth.</li>
      <li><strong>Resend</strong> — transactional and daily-briefing email delivery.</li>
      <li><strong>OpenAI / Anthropic / Google</strong> — to generate recommendation text from de-identified prompts. Prompts do not include raw customer PII.</li>
      <li><strong>MongoDB Atlas / cloud hosting providers</strong> — for encrypted-at-rest storage and compute.</li>
    </ul>
    <p>We do not sell personal information.</p>

    <h2>4. Data Retention</h2>
    <p>
      We retain account and merchant data while your account is active. If
      you uninstall the app or close your account, we delete or anonymize
      personal data within 30 days, except where retention is required by law
      (e.g., tax records) or for legitimate fraud-prevention purposes.
    </p>

    <h2>5. Your Rights (GDPR / CCPA / Shopify Mandatory Requests)</h2>
    <p>
      You may request access, correction, export, or deletion of your
      personal data at any time by emailing{" "}
      <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. We honor
      Shopify’s mandatory GDPR webhooks{" "}
      (<code>customers/data_request</code>, <code>customers/redact</code>,{" "}
      <code>shop/redact</code>) within the 30-day window.
    </p>

    <h2>6. Security</h2>
    <p>
      All traffic is encrypted in transit (TLS 1.2+). Access tokens are
      stored encrypted at rest. Access to production data is restricted to
      authorized personnel via MFA and audit logging.
    </p>

    <h2>7. International Transfers</h2>
    <p>
      We may transfer and process data outside your country of residence,
      including to the United States. We rely on Standard Contractual
      Clauses or equivalent safeguards where required.
    </p>

    <h2>8. Children</h2>
    <p>
      The Service is not directed to children under 16. We do not knowingly
      collect personal information from children.
    </p>

    <h2>9. Changes</h2>
    <p>
      We may update this policy from time to time. Material changes will be
      notified via email to active merchants and posted on this page with a
      revised “Last updated” date.
    </p>

    <h2>10. Contact</h2>
    <p>
      For any privacy question or to exercise a right, email{" "}
      <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> or visit{" "}
      <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer">{PLATFORM_URL}</a>.
    </p>
  </Shell>
);

export const Terms = () => (
  <Shell title="Terms of Service" lastUpdated="February 1, 2026" testid="terms-page">
    <p>
      These Terms of Service (“Terms”) govern your access to and use of the{" "}
      {COMPANY} website, Shopify application, and admin dashboard
      (collectively, the “Service”). By installing the app or using the
      Service, you agree to these Terms.
    </p>

    <h2>1. The Service</h2>
    <p>
      {COMPANY} provides an autonomous revenue operator for Shopify
      merchants — surfacing what changed, why, and what to do next, then
      applying approved fixes in one click. The Service is provided “as is”
      and is continuously evolving.
    </p>

    <h2>2. Accounts &amp; Eligibility</h2>
    <ul>
      <li>You must be at least 18 years old and the authorized operator of the Shopify store you connect.</li>
      <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
      <li>You agree to provide accurate billing information for paid tiers.</li>
    </ul>

    <h2>3. Plans &amp; Billing</h2>
    <p>
      The Service is currently available as a free forever tier. Paid Pro
      tiers (Basic — $4.99/mo and Operator — $9.99/mo) are launching soon
      and will be billed monthly through the Shopify billing API.
      Subscriptions will renew automatically until cancelled. You may
      cancel anytime from your account or by uninstalling the app from
      Shopify; you will retain access until the end of the current billing
      period. Fees are non-refundable except where required by law.
    </p>

    <h2>4. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Reverse engineer, decompile, or attempt to extract source code or model weights.</li>
      <li>Use the Service to violate any law, infringe IP, or send unsolicited bulk email.</li>
      <li>Interfere with or disrupt the Service or attempt to bypass usage or rate limits.</li>
      <li>Resell, sublicense, or white-label the Service without written permission.</li>
    </ul>

    <h2>5. Your Data &amp; License to Operate</h2>
    <p>
      You retain all rights to your store data. You grant {COMPANY} a
      limited, non-exclusive license to access, process, and analyze your
      commerce and ad data solely to provide and improve the Service. We do
      not sell your data. See our{" "}
      <Link to="/privacy" data-testid="terms-to-privacy">Privacy Policy</Link>{" "}
      for details.
    </p>

    <h2>6. AI Recommendations</h2>
    <p>
      The Service generates AI-powered recommendations. While we work to
      make them accurate, recommendations are decision-support, not
      financial advice. You are solely responsible for any “Approve” action
      you authorize. We are not liable for outcomes resulting from actions
      you approve.
    </p>

    <h2>7. Third-Party Services</h2>
    <p>
      The Service integrates with Shopify, Google Ads, Resend, and other
      third parties. Your use of these services is governed by their own
      terms. {COMPANY} is not responsible for outages or changes in
      third-party APIs.
    </p>

    <h2>8. Termination</h2>
    <p>
      You may stop using the Service at any time by uninstalling the app
      from your Shopify admin. We may suspend or terminate access for
      breach of these Terms, non-payment, or risk to the platform. Upon
      termination, the rights granted to you under these Terms end
      immediately.
    </p>

    <h2>9. Disclaimers</h2>
    <p>
      THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES
      OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS
      FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
    </p>

    <h2>10. Limitation of Liability</h2>
    <p>
      TO THE FULLEST EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} WILL
      NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
      OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS OR REVENUE. OUR
      AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THE SERVICE WILL
      NOT EXCEED THE GREATER OF (A) THE FEES YOU PAID US IN THE 12 MONTHS
      PRECEDING THE CLAIM, OR (B) USD&nbsp;$100.
    </p>

    <h2>11. Indemnification</h2>
    <p>
      You agree to indemnify and hold {COMPANY} harmless from any claim
      arising out of your use of the Service, your violation of these
      Terms, or your violation of any third-party right.
    </p>

    <h2>12. Governing Law</h2>
    <p>
      These Terms are governed by the laws of the State of Delaware, USA,
      without regard to conflict of law principles. Disputes will be
      resolved exclusively in the state or federal courts located in
      Delaware.
    </p>

    <h2>13. Changes</h2>
    <p>
      We may update these Terms from time to time. Material changes will
      be notified via email or in-app notice at least 14 days before they
      take effect.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions? Email{" "}
      <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a> or{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
    </p>
  </Shell>
);

export default Privacy;
